import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { gdprApi, consentApi, policyApi, Consent, PolicyAcceptance } from "../services/gdprApi";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";

export const GdprDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { userInfo, user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [consents, setConsents] = useState<Consent[]>([]);
  const [policyAcceptances, setPolicyAcceptances] = useState<PolicyAcceptance[]>([]);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [deletionForm, setDeletionForm] = useState({
    requestType: "soft_delete" as "soft_delete" | "hard_delete" | "anonymize",
    retentionPeriodDays: 0,
  });

  useEffect(() => {
    if (user || userInfo) {
      loadConsents();
      loadPolicyAcceptances();
      loadDeletionRequests();
    }
  }, [user, userInfo]);

  const loadConsents = async (): Promise<void> => {
    try {
      const response = await consentApi.getMyConsents();
      setConsents(response.consents);
    } catch (err: any) {
      console.error("Error loading consents:", err);
    }
  };

  const loadPolicyAcceptances = async (): Promise<void> => {
    try {
      const response = await policyApi.getMyPolicyAcceptances();
      setPolicyAcceptances(response.acceptances);
    } catch (err: any) {
      console.error("Error loading policy acceptances:", err);
    }
  };

  const loadDeletionRequests = async (): Promise<void> => {
    try {
      // Note: This would require a GET /api/gdpr/deletion-requests endpoint
      // For now, we'll implement a simpler version that shows the latest request
      // The full implementation would list all requests
      // setDeletionRequests([]); // TODO: Implement when endpoint is available
    } catch (err: any) {
      console.error("Error loading deletion requests:", err);
    }
  };

  const handleExportData = async (): Promise<void> => {
    try {
      setExporting(true);
      setError(null);
      await gdprApi.exportDataAsFile();
      setSuccess(t("gdpr.exportSuccess"));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || t("gdpr.exportError"));
      setTimeout(() => setError(null), 5000);
    } finally {
      setExporting(false);
    }
  };

  const handleRequestDeletion = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      setDeletionLoading(true);
      setError(null);
      const response = await gdprApi.requestDeletion({
        requestType: deletionForm.requestType,
        retentionPeriodDays: deletionForm.retentionPeriodDays || undefined,
      });
      setSuccess(t("gdpr.deletionRequestSuccess", { requestId: response.requestId }));
      setDeletionForm({
        requestType: "soft_delete",
        retentionPeriodDays: 0,
      });
      setTimeout(() => {
        setSuccess(null);
        loadDeletionRequests();
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || t("gdpr.deletionRequestError"));
      setTimeout(() => setError(null), 5000);
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleRevokeConsent = async (consentType: string): Promise<void> => {
    try {
      await consentApi.revokeConsent(consentType);
      setSuccess(t("gdpr.consentRevokedSuccess", { consentType }));
      setTimeout(() => {
        setSuccess(null);
        loadConsents();
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t("gdpr.consentRevokeError"));
      setTimeout(() => setError(null), 5000);
    }
  };

  if (!user && !userInfo) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Heading size="lg">{t("gdpr.loginRequired")}</Heading>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("gdpr.dashboardTitle")}</Heading>

        {error && (
          <Card padding="md" className="bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
          </Card>
        )}

        {success && (
          <Card padding="md" className="bg-green-50 border-green-200">
            <p className="text-green-800">{success}</p>
          </Card>
        )}

        {/* Data Export Section */}
        <Card padding="md">
          <Stack spacing="md">
            <Heading size="lg">{t("gdpr.exportTitle")}</Heading>
            <p className="text-gray-600">
              {t("gdpr.exportDescription")}
            </p>
            <Button
              onClick={handleExportData}
              disabled={exporting}
              variant="primary"
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {t("gdpr.exporting")}
                </>
              ) : (
                t("gdpr.exportButton")
              )}
            </Button>
          </Stack>
        </Card>

        {/* Deletion Request Section */}
        <Card padding="md">
          <Stack spacing="md">
            <Heading size="lg">{t("gdpr.deletionTitle")}</Heading>
            <p className="text-gray-600">
              {t("gdpr.deletionDescription")}
            </p>
            <form onSubmit={handleRequestDeletion}>
              <Stack spacing="md">
                <div>
                  <label className="block text-sm font-medium mb-2">{t("gdpr.deletionType")}</label>
                  <select
                    value={deletionForm.requestType}
                    onChange={(e) =>
                      setDeletionForm({
                        ...deletionForm,
                        requestType: e.target.value as "soft_delete" | "hard_delete" | "anonymize",
                      })
                    }
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="soft_delete">{t("gdpr.deletionTypeSoft")}</option>
                    <option value="anonymize">{t("gdpr.deletionTypeAnonymize")}</option>
                    <option value="hard_delete">{t("gdpr.deletionTypeHard")}</option>
                  </select>
                </div>

                {deletionForm.requestType === "hard_delete" && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t("gdpr.retentionPeriodLabel")}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="2555"
                      value={deletionForm.retentionPeriodDays}
                      onChange={(e) =>
                        setDeletionForm({
                          ...deletionForm,
                          retentionPeriodDays: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {t("gdpr.retentionPeriodHelp")}
                    </p>
                  </div>
                )}

                <Button type="submit" disabled={deletionLoading} variant="danger">
                  {deletionLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t("gdpr.processing")}
                    </>
                  ) : (
                    t("gdpr.requestDeletionButton")
                  )}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Card>

        {/* Consent Management Section */}
        <Card padding="md">
          <Stack spacing="md">
            <Heading size="lg">{t("gdpr.consentManagementTitle")}</Heading>
            <p className="text-gray-600">
              {t("gdpr.consentManagementDescription")}
            </p>
            {consents.length === 0 ? (
              <p className="text-gray-500">{t("gdpr.noConsentsFound")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">{t("gdpr.tableType")}</th>
                      <th className="text-left p-2">{t("gdpr.tableStatus")}</th>
                      <th className="text-left p-2">{t("gdpr.tableVersion")}</th>
                      <th className="text-left p-2">{t("gdpr.tableGranted")}</th>
                      <th className="text-left p-2">{t("gdpr.tableRevoked")}</th>
                      <th className="text-left p-2">{t("gdpr.tableActions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consents.map((consent) => (
                      <tr key={consent.id} className="border-b">
                        <td className="p-2">{consent.consentType}</td>
                        <td className="p-2">
                          {consent.granted && !consent.revokedAt ? (
                            <span className="text-green-600">{t("gdpr.statusActive")}</span>
                          ) : (
                            <span className="text-red-600">{t("gdpr.statusRevoked")}</span>
                          )}
                        </td>
                        <td className="p-2">{consent.version}</td>
                        <td className="p-2">{new Date(consent.createdAt).toLocaleDateString()}</td>
                        <td className="p-2">
                          {consent.revokedAt ? new Date(consent.revokedAt).toLocaleDateString() : "-"}
                        </td>
                        <td className="p-2">
                          {consent.granted && !consent.revokedAt && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRevokeConsent(consent.consentType)}
                            >
                              {t("gdpr.revokeButton")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Stack>
        </Card>

        {/* Policy Acceptance Section */}
        <Card padding="md">
          <Stack spacing="md">
            <Heading size="lg">{t("gdpr.policyAcceptancesTitle")}</Heading>
            <p className="text-gray-600">
              {t("gdpr.policyAcceptancesDescription")}
            </p>
            {policyAcceptances.length === 0 ? (
              <p className="text-gray-500">{t("gdpr.noPolicyAcceptancesFound")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">{t("gdpr.tablePolicyType")}</th>
                      <th className="text-left p-2">{t("gdpr.tableVersion")}</th>
                      <th className="text-left p-2">{t("gdpr.tableAcceptedAt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policyAcceptances.map((acceptance) => (
                      <tr key={acceptance.id} className="border-b">
                        <td className="p-2">{acceptance.policyType}</td>
                        <td className="p-2">{acceptance.version}</td>
                        <td className="p-2">{new Date(acceptance.acceptedAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Stack>
        </Card>

        {/* Information Section */}
        <Card padding="md" className="bg-blue-50 border-blue-200">
          <Stack spacing="sm">
            <Heading size="md">{t("gdpr.rightsTitle")}</Heading>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li><strong>{t("gdpr.rightAccess")}:</strong> {t("gdpr.rightAccessDescription")}</li>
              <li><strong>{t("gdpr.rightErasure")}:</strong> {t("gdpr.rightErasureDescription")}</li>
              <li><strong>{t("gdpr.rightWithdrawConsent")}:</strong> {t("gdpr.rightWithdrawConsentDescription")}</li>
              <li><strong>{t("gdpr.rightObject")}:</strong> {t("gdpr.rightObjectDescription")}</li>
              <li><strong>{t("gdpr.rightDataPortability")}:</strong> {t("gdpr.rightDataPortabilityDescription")}</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              {t("gdpr.rightsContact")}
            </p>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};

